"use client";

import { useTranslations, useLocale } from "next-intl";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";

export interface GoalRow {
  year: number;
  target: number;
  actual: number | null;
  diff: number | null;
  deviationPct: number | null;
  status: "ahead" | "on-track" | "behind" | null;
}

interface Props {
  data: GoalRow[];
  baseCurrency: string;
}

function barColor(deviationPct: number | null): string {
  if (deviationPct === null) return "#4B5563";
  if (deviationPct >= 0) return "#22C55E";
  if (deviationPct >= -10) return "#F59E0B";
  return "#EF4444";
}

interface TooltipPayload {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}

function DeviationTooltip({ active, payload, label }: TooltipPayload) {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div className="bg-surface-2 border border-border rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-muted mb-1">{label}</p>
      <p className="font-semibold text-text">
        {val > 0 ? "+" : ""}
        {val.toFixed(1)}%
      </p>
    </div>
  );
}

export function GoalDeviationChart({ data, baseCurrency: _baseCurrency }: Props) {
  const t = useTranslations("Goals");
  const locale = useLocale();
  void locale;

  const chartData = data
    .filter((r) => r.deviationPct !== null)
    .map((r) => ({ year: r.year, deviation: r.deviationPct as number }));

  return (
    <div className="bg-surface rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-3">
        {t("chartDeviation")}
      </p>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <XAxis
            dataKey="year"
            tick={{ fill: "#4A6A8A", fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fill: "#4A6A8A", fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${v > 0 ? "+" : ""}${v.toFixed(0)}%`}
            width={44}
          />
          <CartesianGrid strokeDasharray="3 3" stroke="#2A3F56" />
          <Tooltip content={<DeviationTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
          <ReferenceLine y={0} stroke="#4A6A8A" strokeWidth={1} />
          <Bar dataKey="deviation" maxBarSize={32} isAnimationActive={false}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={barColor(entry.deviation)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
